import {
    ChangeDetectionStrategy,
    Component,
    type InputSignal,
    input,
    computed,
    type Signal,
    effect,
    type OnInit,
    DestroyRef,
    inject,
} from '@angular/core';
import { ReactiveFormsModule, type FormControl, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { DistrictMode } from '@pecan/shared/constants/address.constants';
import { FieldLabelComponent } from '@pecan/shared/components/field-label/field-label.component';
import { InvalidLabelComponent } from '@pecan/shared/components/invalid-label/invalid-label.component';
import { MAX_FIELD_LENGTH_DICTIONARY } from '@pecan/shared/constants/max-field-length-dictionary.constant';
import { lettersSpacesRegExp } from '@pecan/shared/constants/letters-spaces-regexp';
import { State } from '../../models/states.enum';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        FieldLabelComponent,
        InvalidLabelComponent,
        MatFormField,
        MatInput,
        MatError,
        MatSelect,
        MatOption,
        NgxTrimDirectiveModule,
        TranslatePipe,
    ],
    selector: 'app-state-input',
    styleUrls: ['./state-input.component.scss'],
    templateUrl: './state-input.component.html',
})
export class StateInputComponent implements OnInit {
    public readonly control: InputSignal<FormControl<string>> = input.required<FormControl<string>>();

    public readonly required: InputSignal<boolean> = input<boolean>(true);

    public readonly districtMode: InputSignal<DistrictMode> = input<DistrictMode>(DistrictMode.State);

    public readonly DISTRICT_MODE: typeof DistrictMode = DistrictMode;

    public states: string[] = Object.values(State);

    public readonly maxLength: Signal<number> = computed(() =>
        this.districtMode() === DistrictMode.State ? MAX_FIELD_LENGTH_DICTIONARY.state : MAX_FIELD_LENGTH_DICTIONARY.province
    );

    public readonly labelKey: Signal<string> = computed(() => (this.districtMode() === DistrictMode.State ? 'state' : 'province'));

    private readonly _destroyRef: DestroyRef = inject(DestroyRef);

    constructor() {
        effect(() => this._applyValidators());
    }

    public ngOnInit(): void {
        this._enforceAlphabeticInputForProvince();
    }

    private _enforceAlphabeticInputForProvince(): void {
        this.control()
            .valueChanges.pipe(
                takeUntilDestroyed(this._destroyRef),
                filter((value: string): value is string => this.districtMode() === DistrictMode.Province && Boolean(value)),
                map((value: string) => {
                    const sanitized = value.replace(/[^A-Za-z ]/g, '');

                    return sanitized === value ? null : sanitized;
                }),
                filter((value: string | null): value is string => value !== null)
            )
            .subscribe((sanitized: string) => this.control().setValue(sanitized, { emitEvent: false }));
    }

    private _applyValidators(): void {
        const mode = this.districtMode();
        const validators = [Validators.maxLength(this.maxLength())];

        if (this.required()) {
            validators.push(Validators.required);
        }

        if (mode === DistrictMode.Province) {
            validators.push(Validators.pattern(lettersSpacesRegExp));
        }

        this.control().setValidators(validators);
        this.control().updateValueAndValidity();
    }
}
